import type { KeyboardEvent, ReactNode } from 'react';
import type { DialogSize } from '@rio-cloud/rio-uikit/components/dialog/Dialog';
import { OPEN_DIALOG_EVENT } from '@rio-cloud/rio-uikit/FrameDialog';
import usePostMessageSender from '@rio-cloud/rio-uikit/usePostMessageSender';

import {
    config,
    isWorkshopParentFrontend,
    PARENT_FRONTEND_CUSTOMER,
    PARENT_FRONTEND_PARAM,
    PARENT_FRONTEND_WORKSHOP,
} from '@/config';
import { WORKSHOP_USER_LOCALE_PARAM, WORKSHOP_USER_TOKEN_PARAM } from '@/configuration';
import { DEFAULT_LOCALE } from '@/configuration/lang/lang';
import { getLocale } from '@/configuration/lang/langSlice';
import { store } from '@/configuration/setup/store';
import { getAccessToken } from '@/configuration/tokenHandling/tokenSlice';
import { MFE_DIALOG_PREFIX } from '@/features/app/dialogs/dialogConstants';

export type RemoteDialogProps = {
    path: string;
    children: ReactNode;
    size?: DialogSize;
    className?: string;
};

export const buildDialogUrl = (path: string, extraParams: Record<string, string> = {}): string => {
    const url = new URL(config.frontend.FRONTEND_URI ?? '');
    const isWorkshop = isWorkshopParentFrontend();

    url.searchParams.set(PARENT_FRONTEND_PARAM, isWorkshop ? PARENT_FRONTEND_WORKSHOP : PARENT_FRONTEND_CUSTOMER);
    if (isWorkshop) {
        url.searchParams.set(WORKSHOP_USER_TOKEN_PARAM, getAccessToken(store.getState()));
        url.searchParams.set(WORKSHOP_USER_LOCALE_PARAM, getLocale(store.getState()) || DEFAULT_LOCALE);
    }
    for (const [key, value] of Object.entries(extraParams)) {
        url.searchParams.set(key, value);
    }
    url.hash = path;
    return url.toString();
};

export const useRemoteDialogOpener = () => {
    const { sendMessageToParent } = usePostMessageSender();

    return (path: string, size: DialogSize = 'md', extraParams: Record<string, string> = {}) => {
        sendMessageToParent({
            type: `${MFE_DIALOG_PREFIX}${OPEN_DIALOG_EVENT}`,
            payload: {
                src: buildDialogUrl(path, extraParams),
                dialogProps: {
                    bsSize: size,
                },
            },
        });
    };
};

const RemoteDialog = ({
    path,
    children,
    size = 'md',
    className = 'cursor-pointer hover-text-underline',
}: RemoteDialogProps) => {
    const openRemoteDialog = useRemoteDialogOpener();

    const openDialog = () => openRemoteDialog(path, size);

    const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openDialog();
        }
    };

    return (
        <span className={className} onClick={openDialog} tabIndex={0} onKeyDown={handleKeyDown} role="button">
            {children}
        </span>
    );
};

export default RemoteDialog;
